"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { useSearchParams } from "next/navigation";

import { Filter } from "@/interfaces/filter.interface";
import useFilterStore from "@/stores/order-filter.store";
import { ListFilter } from "lucide-react";
import { FILTER_DISPLAY_TITLES } from "@/constants/filter";

type FilterValue = string | undefined;
type FilterMap = Record<string, FilterValue>;

const shallowEqual = (a: FilterMap, b: FilterMap) => {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak)
    if ((a[k] ?? undefined) !== (b[k] ?? undefined)) return false;
  return true;
};

const FilterSidebar = ({
  filterOptions,
  position = "right",
}: {
  filterOptions: Record<string, Filter[]>;
  position?: "left" | "right";
}) => {
  const [visible, setVisible] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterMap>({});
  const searchParams = useSearchParams();

  const globalFilter = useFilterStore((s) => s.filter);
  const updateGlobalFilter = useFilterStore((s) => s.updateFilter);

  // ---- init from options: default selected for single-select; undefined for multi ----
  const initialFilters = useMemo<FilterMap>(() => {
    const params: FilterMap = {};
    Object.keys(filterOptions).forEach((key) => {
      const isMulti = filterOptions[key][0]?.type === "multi-select";
      if (isMulti) {
        params[key] = undefined;
      } else {
        const selectedOption = filterOptions[key].find((f) => f.selected);
        params[key] = selectedOption?.value;
      }
    });
    return params;
  }, [filterOptions]);

  // ---- read from URL ----
  const filterInParams = useMemo<FilterMap>(() => {
    const params: FilterMap = {};
    searchParams.forEach((value, key) => {
      if (
        Object.keys(filterOptions).includes(key) &&
        value &&
        value !== "undefined" &&
        value !== "null" &&
        value !== ""
      ) {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams, filterOptions]);

  // ---- one-time sync to global: initial + URL ----
  useEffect(() => {
    const merged = { ...initialFilters, ...filterInParams };
    updateGlobalFilter(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters, filterInParams]);

  // ---- when open sidebar: copy current global -> local (so Cancel can discard) ----
  useEffect(() => {
    if (visible) {
      // ensure all keys exist in local
      const base: FilterMap = {};
      for (const k of Object.keys(filterOptions)) {
        base[k] = (globalFilter as FilterMap)[k];
      }
      setLocalFilters(base);
    }
  }, [visible, globalFilter, filterOptions]);

  // ---- derived: badge count from global (đang áp dụng) ----
  const filteredCount = useMemo(() => {
    return Object.keys(globalFilter).filter(
      (key) =>
        globalFilter[key] &&
        key in filterOptions &&
        String(globalFilter[key])?.length,
    ).length;
  }, [globalFilter, filterOptions]);

  // ---- local handlers (KHÔNG đụng global cho tới khi bấm Apply) ----
  const onLocalChange = useCallback(
    (key: string, value: string, type: Filter["type"] = "single-select") => {
      setLocalFilters((prev) => {
        if (type === "multi-select") {
          const current = String(prev[key] || "")
            .split(",")
            .filter(Boolean);
          const next = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
          return { ...prev, [key]: next.join(",") || undefined };
        }
        return { ...prev, [key]: value };
      });
    },
    [],
  );

  const handleLocalMultiAll = (
    key: string,
    options: Filter[],
    select: boolean,
  ) => {
    setLocalFilters((prev) => {
      if (select) {
        const allValues = options.map((o) => o.value).join(",");
        return { ...prev, [key]: allValues };
      }
      return { ...prev, [key]: undefined };
    });
  };

  const handleLocalClearAll = useCallback(() => {
    const cleared: Record<string, undefined> = {};
    Object.keys(filterOptions).forEach((key) => (cleared[key] = undefined));
    setLocalFilters((prev) => ({ ...prev, ...cleared }));
  }, [filterOptions]);

  const handleApply = () => {
    updateGlobalFilter(localFilters);
    setVisible(false);
  };

  const handleCancel = () => {
    setVisible(false); // local bị bỏ, vì sẽ sync lại từ global khi mở lần sau
  };

  const isDirty = useMemo(
    () => !shallowEqual(localFilters, globalFilter as FilterMap),
    [localFilters, globalFilter],
  );

  // ---- UI item renderers ----
  const RenderMultiSelectFilterItem = ({
    option,
    onSelect,
    selected,
  }: {
    option: Filter;
    onSelect: (value: string) => void;
    selected: boolean;
  }) => (
    <div
      className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-100"
      onClick={() => onSelect(option.value)}
    >
      <div
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors duration-200 ${
          selected
            ? "border-primary-500 bg-primary-500"
            : "border-gray-300 bg-white"
        }`}
      >
        {selected && (
          <i className="pi pi-check p-1 text-sm font-bold text-white"></i>
        )}
      </div>
      <span className="text-sm text-gray-700">{option.title}</span>
    </div>
  );

  const RenderSingleSelectFilterItem = ({
    option,
    onSelect,
    selected,
  }: {
    option: Filter;
    onSelect: (value: string) => void;
    selected: boolean;
  }) => (
    <p
      className="flex w-full cursor-pointer items-center gap-2 rounded-md p-2 px-3 text-sm"
      style={{
        backgroundColor: selected ? (option.color ?? "#ccc") : "#f4f4f4",
      }}
      onClick={() => onSelect(option.value)}
    >
      {option.icon ? (
        <i className={`${option.icon} ${selected ? "text-white" : ""}`}></i>
      ) : (
        <i
          className="pi pi-circle"
          style={{ color: selected ? "#fff" : (option.color ?? "#ccc") }}
        ></i>
      )}
      <span style={{ color: selected ? "#fff" : "#000" }}>{option.title}</span>
    </p>
  );

  // ---- render ----
  return (
    <div className="relative h-max w-max">
      <div className="relative">
        <Button
          icon={<ListFilter strokeWidth={1.5} />}
          onClick={() => setVisible(true)}
          className="cursor-pointer"
          aria-label="Open filters"
          label="Filter"
        />

        {filteredCount > 0 && (
          <Badge
            value={filteredCount}
            className="absolute -top-2 -right-2"
            severity="danger"
          />
        )}
      </div>

      <Sidebar
        position={position}
        visible={visible}
        onHide={handleCancel}
        header={
          <h3 className="flex items-center justify-between text-lg font-bold">
            Filters
            <span
              className="ml-3 cursor-pointer pt-1 text-xs font-normal text-gray-500 underline hover:text-gray-700"
              onClick={handleLocalClearAll}
            >
              Clear All
            </span>
          </h3>
        }
        dismissable
        showCloseIcon
        blockScroll
        closeOnEscape
        className="sm:w-32rem md:w-34rem lg:w-36rem w-full"
        style={{ width: "25rem" }}
      >
        <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
          {Object.entries(filterOptions).map(([key, options], index) => {
            const filterType = options[0]?.type || "single-select";
            const selectedValues = String(localFilters[key] || "")
              .split(",")
              .filter(Boolean);

            return (
              <div
                key={key}
                className={`py-2 ${index !== 0 && "border-t"} border-gray-200`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="block text-sm font-semibold text-gray-700">
                    {FILTER_DISPLAY_TITLES[key]}
                  </span>
                  {filterType === "multi-select" && (
                    <div className="flex gap-3">
                      <span
                        className="cursor-pointer text-xs font-normal text-gray-500 hover:text-gray-700 hover:underline"
                        onClick={() => handleLocalMultiAll(key, options, true)}
                      >
                        Select All
                      </span>
                      <span
                        className="cursor-pointer text-xs font-normal text-gray-500 hover:text-gray-700 hover:underline"
                        onClick={() => handleLocalMultiAll(key, options, false)}
                      >
                        Clear
                      </span>
                    </div>
                  )}
                </div>

                {filterType === "multi-select" ? (
                  <div className="flex flex-col gap-1">
                    {options.map((option) => (
                      <RenderMultiSelectFilterItem
                        key={option.value}
                        option={option}
                        onSelect={(value) =>
                          onLocalChange(key, value, "multi-select")
                        }
                        selected={selectedValues.includes(option.value)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {options.map((option) => (
                      <RenderSingleSelectFilterItem
                        key={option.value}
                        option={option}
                        onSelect={(value) =>
                          onLocalChange(key, value, "single-select")
                        }
                        selected={localFilters[key] === option.value}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="mt-4 flex w-full">
          <Button
            label="Apply"
            onClick={handleApply}
            disabled={!isDirty}
            className="w-full"
          />
        </div>
      </Sidebar>
    </div>
  );
};

export default FilterSidebar;
